import { ChevronDownIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { FC, useEffect } from "react";
import usePolling from "../../hooks/usePolling";
import { IBoardingTrip } from "../../interfaces/endpoints/IBoarding.types";
import { getDepartures } from "../../services/Boarding.service";

type Props = {
  trips: IBoardingTrip[];
  bus: IBoardingTrip | null;
  refreshKey?: number;
  onChange: (tripId: number | null) => void;
};

const POLL_MS = 5000;

const routeOf = (trip: IBoardingTrip) => `${trip.from} → ${trip.to}`;

// The bus being scanned, over the bottom of the camera so the page never shifts when it appears. The
// control is a native select under a label, so a phone opens its own picker.
const BusStrip: FC<Props> = ({ trips, bus, refreshKey, onChange }) => {
  const { data: departures, reload } = usePolling(
    () => (bus ? getDepartures(dayjs().format("YYYY-MM-DD")).then((r) => r.data) : Promise.resolve([])),
    POLL_MS,
    [bus?.tripId]
  );
  useEffect(() => {
    if (refreshKey) reload();
  }, [refreshKey, reload]);

  if (!bus && !trips.length) return null;
  const counts = bus && departures?.find((departure) => departure.tripId === bus.tripId);
  const routes = Array.from(new Set(trips.map(routeOf)));

  return (
    <div
      className={`absolute bottom-0 z-10 flex items-center gap-2 px-3 py-1.5 text-white ${
        bus ? "inset-x-0 bg-black-900/65 backdrop-blur-sm" : "right-0"
      }`}
    >
      {bus && (
        <div className="min-w-0 flex-1 text-sm leading-snug">
          <p className="truncate font-semibold" title={routeOf(bus)}>
            {bus.departureTime} → {bus.to}
          </p>
          <p>
            Skanuar{" "}
            <span className="font-bold tabular-nums">
              {counts ? `${counts.boarded} / ${counts.boarded + counts.toBoard}` : "– / –"}
            </span>
          </p>
        </div>
      )}
      <label
        className="relative flex min-h-[44px] shrink-0 items-center gap-1 rounded-full bg-black-900/40 px-3
          text-sm font-semibold ring-1 ring-white/60 focus-within:ring-2"
      >
        Nisja
        <ChevronDownIcon aria-hidden className="h-4 w-4" />
        <select
          aria-label="Nisja"
          value={bus?.tripId ?? ""}
          onChange={(event) => onChange(event.target.value ? Number(event.target.value) : null)}
          className="absolute inset-0 cursor-pointer text-base opacity-0"
        >
          <option value="">Nga bileta e radhës</option>
          {routes.map((route) => (
            <optgroup key={route} label={route}>
              {trips
                .filter((trip) => routeOf(trip) === route)
                .map((trip) => (
                  <option key={trip.tripId} value={trip.tripId}>
                    {trip.departureTime}
                  </option>
                ))}
            </optgroup>
          ))}
        </select>
      </label>
    </div>
  );
};

export default BusStrip;
